import type { ReactNode } from 'react';
import { Vitrina } from './Vitrina';
import './onboardingMarca.css';
import './marcoDesktop.css';

/** El encabezado de los pasos: "Paso N de M", el nombre del paso y la barra. */
export interface PasosDelMarco {
  /** Desde 0. */
  actual: number;
  total: number;
  nombre: string;
  /** Va a la derecha del nombre: "Salir" en "Añadir nueva marca", "Volver" en el cuestionario. */
  accion?: ReactNode;
}

/**
 * El marco visual del onboarding de marca, compartido con el cuestionario corto
 * (ADR-2026-10-31-el-cuestionario-corto-con-el-diseno-del-de-marca): el fondo, en desktop el
 * recuadro con la vitrina a los costados, y el encabezado con los pasos. La lógica de cada
 * flujo queda en su layout.
 *
 * Sin `pasos`, el encabezado queda vacío pero ocupa su lugar: las pantallas miden su alto con
 * `100dvh - var(--om-barra)`.
 */
export function MarcoDelOnboarding({
  pasos,
  ancho = false,
  children,
  despues,
}: {
  pasos: PasosDelMarco | null;
  /** El tablero de la marca: el recuadro más ancho. */
  ancho?: boolean;
  /** Lo que va adentro del recuadro, debajo del encabezado. */
  children: ReactNode;
  /** Lo que va afuera del recuadro (modales). */
  despues?: ReactNode;
}) {
  return (
    <div className={`om-marco min-h-[100dvh] bg-[#FAFAFA] text-[#18181B] ${ancho ? 'om-marco--tablero' : ''}`}>
      {/* En desktop el paso va en un recuadro blanco con borde azul, sobre el fondo de la
          marca oscurecido (ADR-2026-10-02-onboarding-v2-recuadro-desktop). En el teléfono,
          `om-fondo-desktop` no se ve y `om-tarjeta-paso` no tiene estilo. */}
      <div className="om-fondo-desktop" aria-hidden="true" />
      {/* En desktop, a los costados: imágenes creadas con Atlas y lo que dicen las marcas
          (ADR-2026-10-02-onboarding-v2-vitrina-desktop). Siempre montada, también en el
          tablero: desmontarla la hacía desaparecer y volver a entrar entre paso y paso. Sólo
          entre 1024 y 1279px el CSS la esconde en el tablero, que ahí necesita el ancho. */}
      <Vitrina />
      <div className={`om-tarjeta-paso ${ancho ? 'om-tarjeta-paso--ancha' : ''}`}>
        {/* Sin fondo ni borde: los pasos van sobre la pantalla misma (el fondo de Cumbre
            ocupa la ventana entera). No es sticky: transparente, el contenido le pasaría
            por debajo. */}
        <header className="relative z-20 mx-auto flex h-[var(--om-barra)] max-w-[640px] flex-col justify-end gap-2.5 px-4 pb-5">
          {pasos && (
            <>
              <p className="flex items-baseline justify-between text-[13px] font-semibold leading-[18px]">
                <span className="text-brand-primary">
                  Paso {pasos.actual + 1} <span className="text-[#98A1C0]">de {pasos.total}</span>
                </span>
                <span className="flex items-baseline gap-3">
                  <span className="text-[#4B5275]">{pasos.nombre}</span>
                  {pasos.accion}
                </span>
              </p>
              <ol className="flex gap-1.5" aria-hidden="true">
                {Array.from({ length: pasos.total }, (_, i) => (
                  <li key={i} className={`om-paso ${i < pasos.actual ? 'is-hecho' : i === pasos.actual ? 'is-actual' : ''}`} />
                ))}
              </ol>
            </>
          )}
        </header>
        {children}
      </div>
      {despues}
    </div>
  );
}

export default MarcoDelOnboarding;
